import * as PropTypes from "prop-types";
import { lazy, Suspense, useEffect } from "react";

// Loaded on demand. Importing all of them eagerly made this file 1128 of the ~1160
// modules the app pulls before it can render anything, for dialogs the first screen
// never opens.
// The three below are named exports, and lazy() wants a module with a default.
const DeleteOutlineSectionsModal = lazy(() =>
  import("layout/proposals/outline").then((m) => ({
    default: m.DeleteOutlineSectionsModal,
  }))
);
const HeadingSyncModal = lazy(() => import("./HeadingSyncModal"));
const HeadingDeleteModal = lazy(() =>
  import("./HeadingSyncModal").then((m) => ({ default: m.HeadingDeleteModal }))
);
const AcronymReportModal = lazy(
  () => import("components/Modal/AcronymReportModal")
);
const AnnotatedAcronymReportSelectionModal = lazy(
  () => import("components/Modal/AnnotatedAcronymReportSelectionModal")
);
const AupAcceptanceModal = lazy(
  () => import("components/Modal/AupAcceptanceModal/AupAcceptanceModal")
);
const CascadeAssigneeModal = lazy(
  () => import("components/Modal/CascadeAssigneeModal")
);
const CascadeStatusModal = lazy(
  () => import("components/Modal/CascadeStatusModal")
);
const ChooseDocumentsModal = lazy(
  () => import("components/Modal/ChooseDocumentsModal")
);
const ChooseFilesModal = lazy(
  () => import("components/Modal/ChooseFilesModal")
);
const CompareDocumentsModal = lazy(
  () => import("components/Modal/CompareDocumentsModal")
);
const ConfirmationModal = lazy(
  () => import("components/Modal/ConfirmationModal")
);
const DateRangeModal = lazy(() => import("components/Modal/DateRangeModal"));
const ErrorModal = lazy(() => import("components/Modal/ErrorModal"));
const ImportFileModal = lazy(() => import("components/Modal/ImportFileModal"));
const SelectFolderModal = lazy(
  () => import("components/Modal/SelectFolderModal")
);
const SelectUploadedDocumentsModal = lazy(
  () => import("components/Modal/SelectUploadedDocumentsModal")
);
const TextFieldModal = lazy(() => import("components/Modal/TextFieldModal"));
const UploadExternalFilesModal = lazy(
  () => import("components/Modal/UploadExternalFilesModal")
);
const UploadFilesModal = lazy(
  () => import("components/Modal/UploadFilesModal")
);
const AboutVtDocsModal = lazy(
  () => import("layout/dashboard/modals/AboutVtDocsModal")
);
const ChangePasswordModal = lazy(
  () => import("layout/dashboard/modals/ChangePasswordModal")
);
const AnnotatedDocumentModal = lazy(
  () => import("layout/dictionarySearch/modals/AnnotatedDocumentModal")
);
const ChangeDictionaryModal = lazy(
  () => import("layout/dictionarySearch/modals/ChangeDictionaryModal")
);
const ExportContentModalXlsx = lazy(
  () => import("layout/dictionarySearch/modals/ExportContentModalXlsx")
);
const DictionarySearchExportMultipleDocumentsModal = lazy(
  () => import("layout/dictionarySearch/modals/ExportMultipleDocumentsModal")
);
const ImportDictionaryModal = lazy(
  () => import("layout/dictionarySearch/modals/ImportDictionaryModal")
);
const CreateNewCategoryModal = lazy(
  () => import("layout/discovery/modals/CreateNewCategoryModal")
);
const CreateNewDictionaryModal = lazy(
  () => import("layout/discovery/modals/CreateNewDictionaryModal")
);
const DiscoveryExportMultipleDocumentsModal = lazy(
  () => import("layout/discovery/modals/ExportMultipleDocumentsModal")
);
const IgnoreTermConfirm = lazy(
  () => import("layout/discovery/modals/IgnoreTermConfirm")
);
const SaveSnapshotModal = lazy(
  () => import("layout/discovery/modals/SaveSnapshotModal")
);
const SelectDocumentModal = lazy(
  () => import("layout/discovery/modals/SelectDocumentModal")
);
const ShredToExcelModal = lazy(
  () => import("layout/discovery/modals/ShredToExcelModal")
);
const DocumentReadabilityReportOptionsModal = lazy(
  () =>
    import("layout/documentReadabilityReport/DocumentReadabilityReportOptionsModal")
);
const ChooseShredDocumentModal = lazy(
  () => import("layout/documentShredding/modals/ChooseShredDocumentModal")
);
const ShredAnnotatedDocumentModal = lazy(
  () => import("layout/documentShredding/modals/ShredAnnotatedDocumentModal")
);
const LastUpdatedCustomRangeModal = lazy(
  () => import("layout/opportunities/ViewModes/LastUpdatedCustomRangeModal")
);
const ImportPromptLibraryCsvModal = lazy(
  () => import("layout/prompts/components/ImportPromptLibraryCsvModal")
);
const OutlineClarificationModal = lazy(
  () => import("layout/proposals/outline/OutlineClarificationModal")
);
const ResetDefaultsModal = lazy(
  () => import("layout/settings/readabilityThresholds/ResetDefaultsModal")
);
const EnableSsoModal = lazy(
  () => import("layout/systemAdmin/authentication/modals/EnableSsoModal")
);
const EnableUserPasswordModal = lazy(
  () =>
    import("layout/systemAdmin/authentication/modals/EnableUsernamePasswordModal")
);
const AddEditLlmProviderModal = lazy(
  () => import("layout/systemAdmin/GenerativeAI/modals/AddEditLlmProviderModal")
);
const DisableAiFeatureModal = lazy(
  () => import("layout/systemAdmin/GenerativeAI/modals/DisableAiFeatureModal")
);
const editUserModal = lazy(
  () => import("layout/systemAdmin/users/modals/EditUserModal")
);
const ImportUsersModal = lazy(
  () => import("layout/systemAdmin/users/modals/ImportUsersModal")
);
const newUserModal = lazy(
  () => import("layout/systemAdmin/users/modals/NewUserModal")
);
const AddUserModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/AddUserModal")
);
const AnonymiseUsersModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/AnonymiseUsersModal")
);
const BannerModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/BannerModal")
);
const CreateWorkspaceWithLicensingModal = lazy(
  () =>
    import("layout/systemAdmin/workspaces/modals/CreateWorkspaceWithLicensingModal")
);
const DeleteWorkspaceModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/DeleteWorkspaceModal")
);
const EditLicensingModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/EditLicensingModal")
);
const EditUserPermissionsModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/EditUserPermissionsModal")
);
const ExportReportModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/ExportReportModal")
);
const PasswordExpiryModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/PasswordExpiryModal")
);
const ReserveLicensesModal = lazy(
  () => import("layout/systemAdmin/workspaces/modals/ReserveLicensesModal")
);
const EditUserAndPermissionsModal = lazy(
  () => import("layout/userAdmin/users/modals/EditUserAndPermissionsModal")
);
const AddInConfirmOutlineInsert = lazy(
  () => import("./AddInConfirmOutlineInsert")
);
const AddInNameClashPrompt = lazy(() => import("./AddInNameClashPrompt"));
const AddInTurnOffAutosave = lazy(
  () => import("./AddInTurnOffAutosave/AddInTurnOffAutosave")
);
const ArchiveEntityModal = lazy(() => import("./ArchiveEntityModal"));
const ArchiveStatusReassignModal = lazy(
  () => import("./ArchiveStatusReassignModal")
);
const ShreddingTemplateFormModal = lazy(
  () => import("./ShreddingTemplateFormModal")
);
const ShreddingTemplateColumnModal = lazy(
  () => import("./ShreddingTemplateColumnModal")
);
const ShreddingTemplateGroupModal = lazy(
  () => import("./ShreddingTemplateGroupModal")
);
const ArchivedByReasonModal = lazy(() => import("./ArchivedByReasonModal"));
const RenameShredColumnModal = lazy(() => import("./RenameShredColumnModal"));
const DuplicateShredColumnModal = lazy(
  () => import("./DuplicateShredColumnModal")
);
const RenameShredGroupModal = lazy(() => import("./RenameShredGroupModal"));
const DuplicateShredGroupModal = lazy(
  () => import("./DuplicateShredGroupModal")
);
const CancelAnalysisModal = lazy(() => import("./CancelAnalysisModal"));
const CollectionReindexProgressModal = lazy(
  () => import("./CollectionReindexProgressModal")
);
const CollectionsModal = lazy(
  () => import("./CollectionsModal/CollectionsModal")
);
const CombineOutlineDocsModal = lazy(() => import("./CombineOutlineDocsModal"));
const ConfirmDocStyleTemplateModal = lazy(
  () => import("./ConfirmDocStyleTemplateModal")
);
const DeleteOutlineModal = lazy(() => import("./DeleteOutlineModal"));
const DetachDocumentModal = lazy(() => import("./DetachDocumentModal"));
const DocumentStaleReloadModal = lazy(
  () => import("./DocumentStaleReloadModal")
);
const DocumentSyncModal = lazy(() => import("./DocumentSyncModal"));
const GatePreviewModal = lazy(() => import("./GatePreviewModal"));
const GenericDeleteModal = lazy(() => import("./GenericDeleteModal"));
const HasUnsavedChangesModal = lazy(() => import("./HasUnsavedChangesModal"));
const KanbanAddStageGateQuestion = lazy(
  () => import("./KanbanAddStageGateQuestion")
);
const KanbanGateMoveModal = lazy(() => import("./KanbanGateMoveModal"));
const LoadingModal = lazy(() => import("./LoadingModal"));
const LockedItemsModal = lazy(() => import("./LockedItemsModal"));
const NewOpportunityChooserModal = lazy(
  () => import("./NewOpportunityChooserModal")
);
const NewProposalChooserModal = lazy(() => import("./NewProposalChooserModal"));
const OpportunityToProposalModal = lazy(
  () => import("./OpportunityToProposalModal")
);
const PipelineByOwnerModal = lazy(() => import("./PipelineByOwnerModal"));
const PipelineFunnelModal = lazy(() => import("./PipelineFunnelModal"));
const PipelineValueByCustomerModal = lazy(
  () => import("./PipelineValueByCustomerModal")
);
const ProposalAssignmentModal = lazy(() => import("./ProposalAssignmentModal"));
const ProposalSectionsAssignmentModal = lazy(
  () => import("./ProposalSectionsAssignmentModal")
);
const RecalcProgressModal = lazy(() => import("./RecalcProgressModal"));
const ScoringSwapWatchwordsListModal = lazy(
  () => import("./ScoringSwapWatchwordsListModal")
);
const SectionStatusModal = lazy(() => import("./SectionStatusModal"));
const SessionTimeoutWarningModal = lazy(
  () => import("./SessionTimeoutWarningModal")
);
const StartWritingProposalOutlineWizard = lazy(
  () => import("./StartWritingProposalOutlineWizard")
);
const TimeInStageModal = lazy(() => import("./TimeInStageModal"));
const TotalPipelineModal = lazy(() => import("./TotalPipelineModal"));
const TrackedOpportunityPickerModal = lazy(
  () => import("./TrackedOpportunityPickerModal")
);
const UnarchiveProposalModal = lazy(() => import("./UnarchiveProposalModal"));
const CustomFieldModal = lazy(
  () => import("./CustomFieldModal/CustomFieldModal")
);
const CustomFieldDeleteModal = lazy(
  () => import("./CustomFieldDeleteModal/CustomFieldDeleteModal")
);

const MODAL_COMPONENTS = {
  AUP_ACCEPTANCE: AupAcceptanceModal,
  SAVE_SNAPSHOT: SaveSnapshotModal,
  CONFIRMATION: ConfirmationModal,
  ADD_USER_TO_WORKSPACE: AddUserModal,
  ERROR: ErrorModal,
  DELETE_WORKSPACE: DeleteWorkspaceModal,
  WORKSPACE_RESERVED_LICENSES: ReserveLicensesModal,
  ANONYMIZE_USERS: AnonymiseUsersModal,
  WORKSPACE_BANNER: BannerModal,
  EDIT_PERMISSIONS: EditUserPermissionsModal,
  EXPORT_REPORT: ExportReportModal,
  NEW_USER: newUserModal,
  EDIT_USER: editUserModal,
  IMPORT_USERS: ImportUsersModal,
  EDIT_USER_AND_PERMISSIONS: EditUserAndPermissionsModal,
  ENABLE_SSO: EnableSsoModal,
  ENABLE_USERNAME_PASSWORD: EnableUserPasswordModal,
  ABOUT_VT_DOCS: AboutVtDocsModal,
  CHANGE_PASSWORD: ChangePasswordModal,
  IMPORT_FILE: ImportFileModal,
  TEXT_FIELD_MODAL: TextFieldModal,
  CHANGE_DICTIONARY: ChangeDictionaryModal,
  EXPORT_MULTIPLE_DOCS: DictionarySearchExportMultipleDocumentsModal,
  EXPORT_CONTENT: ExportContentModalXlsx,
  IMPORT_DICTIONARY: ImportDictionaryModal,
  ANNOTATED_DOCUMENT: AnnotatedDocumentModal,
  SHRED_ANNOTATED_DOCUMENT: ShredAnnotatedDocumentModal,
  CHOOSE_SHRED_DOCUMENT: ChooseShredDocumentModal,
  SHRED_TO_EXCEL: ShredToExcelModal,
  DISCOVERY_EXPORT_MULTIPLE_DOCS: DiscoveryExportMultipleDocumentsModal,
  RESET_DEFAULTS_THRESHOLDS: ResetDefaultsModal,
  IGNORE_TERM_CONFIRM: IgnoreTermConfirm,
  CREATE_NEW_DICTIONARY: CreateNewDictionaryModal,
  UPLOAD_FILES: UploadFilesModal,
  UPLOAD_EXTERNAL_FILES: UploadExternalFilesModal,
  SELECT_FOLDER: SelectFolderModal,
  COMPARE_DOCUMENTS: CompareDocumentsModal,
  CHOOSE_DOCUMENTS: ChooseDocumentsModal,
  ACRONYM_REPORT: AcronymReportModal,
  DATE_RANGE: DateRangeModal,
  EDIT_LICENSING: EditLicensingModal,
  PASSWORD_EXPIRY: PasswordExpiryModal,
  CREATE_WORKSPACE_WITH_LICENSING: CreateWorkspaceWithLicensingModal,
  CREATE_NEW_CATEGORY: CreateNewCategoryModal,
  SELECT_DOCUMENT: SelectDocumentModal,
  DOCUMENT_READABILITY_REPORT_OPTIONS: DocumentReadabilityReportOptionsModal,
  HAS_UNSAVED_CHANGES: HasUnsavedChangesModal,
  DOCUMENT_STALE_RELOAD: DocumentStaleReloadModal,
  CHOOSE_FILES: ChooseFilesModal,
  GENERIC_DELETE_MODAL: GenericDeleteModal,
  CANCEL_ANALYSIS_MODAL: CancelAnalysisModal,
  SELECT_UPLOADED_DOCUMENTS: SelectUploadedDocumentsModal,
  ANNOTATED_ACRONYM_REPORT_SELECTION: AnnotatedAcronymReportSelectionModal,
  LOADING_MODAL: LoadingModal,
  COLLECTIONS_MODAL: CollectionsModal,
  LAST_UPDATED_CUSTOM_RANGE: LastUpdatedCustomRangeModal,
  HEADING_SYNC_MODAL: HeadingSyncModal,
  START_WRITING_PROPOSAL_OUTLINE_WIZARD: StartWritingProposalOutlineWizard,
  HEADING_DELETE_MODAL: HeadingDeleteModal,
  DETACH_DOCUMENT_MODAL: DetachDocumentModal,
  DOCUMENT_SYNC_PROGRESS: DocumentSyncModal,
  COLLECTION_REINDEX_PROGRESS: CollectionReindexProgressModal,
  ARCHIVE_ENTITY_MODAL: ArchiveEntityModal,
  UNARCHIVE_PROPOSAL: UnarchiveProposalModal,
  ARCHIVE_STATUS_REASSIGN_MODAL: ArchiveStatusReassignModal,
  SHREDDING_TEMPLATE_FORM: ShreddingTemplateFormModal,
  SHREDDING_TEMPLATE_COLUMN: ShreddingTemplateColumnModal,
  SHREDDING_TEMPLATE_GROUP: ShreddingTemplateGroupModal,
  RENAME_SHRED_COLUMN: RenameShredColumnModal,
  DUPLICATE_SHRED_COLUMN: DuplicateShredColumnModal,
  RENAME_SHRED_GROUP: RenameShredGroupModal,
  DUPLICATE_SHRED_GROUP: DuplicateShredGroupModal,
  IMPORT_PROMPT_LIBRARY_CSV: ImportPromptLibraryCsvModal,
  SCORING_SWAP_WATCHWORDS_LIST: ScoringSwapWatchwordsListModal,
  ADD_IN_CONFIRM_OUTLINE_INSERT: AddInConfirmOutlineInsert,
  ADD_IN_NAME_CLASH_PROMPT: AddInNameClashPrompt,
  ADD_IN_TURN_OFF_AUTOSAVE: AddInTurnOffAutosave,
  SESSION_TIMEOUT_WARNING: SessionTimeoutWarningModal,
  NEW_OPPORTUNITY_CHOOSER: NewOpportunityChooserModal,
  NEW_PROPOSAL_CHOOSER: NewProposalChooserModal,
  PIPELINE_BY_OWNER: PipelineByOwnerModal,
  PIPELINE_FUNNEL: PipelineFunnelModal,
  PIPELINE_VALUE_BY_CUSTOMER: PipelineValueByCustomerModal,
  SECTION_STATUS: SectionStatusModal,
  TIME_IN_STAGE: TimeInStageModal,
  TOTAL_PIPELINE: TotalPipelineModal,
  TRACKED_OPPORTUNITY_PICKER: TrackedOpportunityPickerModal,
  RECALC_PROGRESS: RecalcProgressModal,
  CLARIFICATION_REQUIRED: OutlineClarificationModal,
  LOCKED_ITEMS_MODAL: LockedItemsModal,
  CONFIRM_DOC_STYLE_TEMPLATE_MODAL: ConfirmDocStyleTemplateModal,
  KANBAN_ADD_STAGE_GATE_QUESTION: KanbanAddStageGateQuestion,
  KANBAN_GATE_MOVE_MODAL: KanbanGateMoveModal,
  GATE_PREVIEW_MODAL: GatePreviewModal,
  DELETE_OUTLINE_SECTIONS_MODAL: DeleteOutlineSectionsModal,
  DELETE_OUTLINE_MODAL: DeleteOutlineModal,
  COMBINE_OUTLINE_DOCS_MODAL: CombineOutlineDocsModal,
  CASCADE_ASSIGNEE_MODAL: CascadeAssigneeModal,
  CASCADE_STATUS_MODAL: CascadeStatusModal,
  OPPORTUNITY_TO_PROPOSAL: OpportunityToProposalModal,
  ARCHIVED_BY_REASON: ArchivedByReasonModal,
  PROPOSAL_ASSIGNMENT: ProposalAssignmentModal,
  PROPOSAL_SECTIONS_ASSIGNMENT: ProposalSectionsAssignmentModal,
  DISABLE_AI_FEATURE_MODAL: DisableAiFeatureModal,
  CUSTOM_FIELD: CustomFieldModal,
  CUSTOM_FIELD_DELETE: CustomFieldDeleteModal,
  LLM_PROVIDER_MODAL: AddEditLlmProviderModal,
};

// Focusing from ModalRoot itself would run before the modal's chunk resolves, when there
// is no dialog in the DOM yet. Inside the Suspense boundary the effect only fires once
// the modal has actually mounted.
const FocusDialog = ({ children }) => {
  useEffect(() => {
    const dialog = document.querySelector("[role='dialog']");
    if (dialog && !dialog.contains(document.activeElement)) {
      dialog.focus();
    }
  }, []);

  return children;
};

FocusDialog.propTypes = {
  children: PropTypes.node,
};

const ModalRoot = ({ modalType, modalProps }) => {
  if (!modalType || !MODAL_COMPONENTS[modalType]) {
    return null;
  }

  const SpecificModal = MODAL_COMPONENTS[modalType];
  // The modalProps can be a list of anything so this has to be spread
  return (
    <Suspense fallback={null}>
      <FocusDialog>
        <SpecificModal {...modalProps} />
      </FocusDialog>
    </Suspense>
  );
};

export default ModalRoot;

ModalRoot.propTypes = {
  modalType: PropTypes.string,
  // The modalProps can be a list of anything so this has to be an object with not types

  modalProps: PropTypes.object,
};
